<template>
  <!--
    SearchPathChips — a global-search results list for the insurance catalog.

    Each result is rendered as its full ancestry expressed as clickable NAME
    path-chips (root › … › parent › NODE). This kills the "1-1.1-1.1.1" outline
    confusion: the user reads real names, and the `›` separator is chrome, not
    text. Chips WRAP (never truncate) so long Thai paths stay fully readable.

    Click contract:
      - clicking an ANCESTOR chip → emits `pickCrumb` (the ancestor node)
      - clicking the LAST (matched) chip → emits `pick` (the matched node)
    The page wires both to the same jump-to-context handler, but the two
    signals are kept distinct so callers can tell "jumped to the hit" from
    "jumped to a containing folder".

    Purely presentational: props in, events out. No Vuex, no API, no element-ui.
  -->
  <div class="spc">
    <!-- Empty state: a non-empty query that matched nothing. -->
    <div v-if="!results.length" class="spc__empty">
      {{ $t("lmg.insure_search_empty") }}
    </div>

    <template v-else>
      <ul class="spc__list" role="listbox">
        <li
          v-for="entry in visibleResults"
          :key="entry.node.ID"
          class="spc__item"
          :class="{ 'is-inactive': isInactive(entry.node) }"
          role="option"
        >
          <!-- path-chips: ancestors (clickable → pickCrumb) then the node
               itself (clickable → pick, emphasized). Separated by a `›`. -->
          <nav class="spc__path" :aria-label="pathLabel(entry)">
            <template v-for="(anc, i) in entry.pathNodes">
              <button
                :key="'a-' + anc.ID"
                type="button"
                class="spc__chip spc__chip--crumb"
                :title="anc.Name"
                @click="$emit('pickCrumb', anc)"
              >
                <span v-html="highlight(displayName(anc.Name, stripPrefix))"></span>
              </button>
              <span
                :key="'sep-' + anc.ID + '-' + i"
                class="spc__sep"
                aria-hidden="true"
              >&rsaquo;</span>
            </template>

            <!-- the matched node — emphasized, last in the path -->
            <button
              type="button"
              class="spc__chip spc__chip--node"
              :title="entry.node.Name"
              @click="$emit('pick', entry.node)"
            >
              <span v-html="highlight(displayName(entry.node.Name, stripPrefix))"></span>
            </button>
          </nav>

          <!-- meta row: dataType chip + leaf/active indicator + inactive tag -->
          <div class="spc__meta">
            <span
              v-if="!hideTypeChip"
              class="spc__dtype"
              :style="{ '--chip-color': metaOf(entry.node).color }"
            >
              <span class="spc__dtype-dot" aria-hidden="true"></span>
              <span class="spc__dtype-label">{{ $t(metaOf(entry.node).key) }}</span>
            </span>

            <span
              class="spc__leaf"
              :class="entry.isLeaf ? 'is-leaf' : 'is-branch'"
              aria-hidden="true"
              :title="entry.isLeaf
                ? $t('lmg.insure_search_leaf')
                : $t('lmg.insure_search_branch')"
            >{{ entry.isLeaf ? "•" : "▸" }}</span>

            <span
              class="spc__state-dot"
              :class="isInactive(entry.node) ? 'is-off' : 'is-on'"
              aria-hidden="true"
            >{{ isInactive(entry.node) ? "○" : "●" }}</span>
            <span v-if="isInactive(entry.node)" class="spc__state-tag">{{
              $t("lmg.insure_inactive_tag")
            }}</span>
          </div>
        </li>
      </ul>

      <!-- "show more" affordance when results are capped for rendering -->
      <button
        v-if="results.length > limit"
        type="button"
        class="spc__more"
        @click="limit += MORE_STEP"
      >
        {{ $t("lmg.insure_search_more") }} ({{ results.length - limit }})
      </button>
    </template>
  </div>
</template>

<script>
import { dataTypeMeta } from "./insDataType";
import { displayName } from "./insName";

const INITIAL_LIMIT = 30;
const MORE_STEP = 30;

export default {
  name: "search-path-chips",
  props: {
    // Array<{ node, pathNodes, isLeaf? }> — pathNodes = ancestors root→parent.
    results: { type: Array, default: () => [] },
    // The raw search term, for case-insensitive bold-highlighting.
    query: { type: String, default: "" },
    // DISPLAY-ONLY: hide leading outline numbers from shown chip names. The
    // search MATCHING (done by the parent) still uses the FULL real name, so a
    // user can still find "1.1.1" by typing it — only the DISPLAYED chip text
    // is stripped. If the matched substring lived in the prefix, no bold shows
    // on the stripped text; that's fine and never crashes.
    stripPrefix: { type: Boolean, default: false },
    // DISPLAY-ONLY: hide the per-result dataType chip (mirrors InsNodeRow).
    hideTypeChip: { type: Boolean, default: false },
  },
  data() {
    return {
      MORE_STEP,
      limit: INITIAL_LIMIT,
    };
  },
  computed: {
    visibleResults() {
      return this.results.slice(0, this.limit);
    },
  },
  watch: {
    // A fresh query should reset the "show more" expansion.
    query() {
      this.limit = INITIAL_LIMIT;
    },
  },
  methods: {
    displayName,
    metaOf(node) {
      return dataTypeMeta(node.DataType);
    },
    isInactive(node) {
      return Number(node.IsActive) === 0;
    },
    pathLabel(entry) {
      const names = entry.pathNodes.map((n) => n.Name);
      names.push(entry.node.Name);
      return names.join(" › ");
    },
    // Bold the substring matching `query` (case-insensitive). Escapes HTML so
    // node names can't inject markup — only our <b> wrapper is allowed through.
    highlight(text) {
      const safe = this.escapeHtml(text == null ? "" : String(text));
      const q = (this.query || "").trim();
      if (!q) return safe;
      const needle = this.escapeHtml(q);
      // Build a case-insensitive matcher on the ESCAPED text so offsets align.
      const re = new RegExp(this.escapeRegExp(needle), "gi");
      return safe.replace(re, (m) => "<b>" + m + "</b>");
    },
    escapeHtml(s) {
      return s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    escapeRegExp(s) {
      return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    },
  },
};
</script>

<style scoped>
.spc {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.spc__empty {
  padding: 24px 8px;
  color: var(--c-text-mute, #8a96a8);
  font-size: 14px;
  text-align: center;
}

.spc__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.spc__item {
  padding: 8px 4px;
  border-bottom: 1px solid var(--c-border, #e4e9f1);
}
.spc__item:last-child {
  border-bottom: 0;
}
.spc__item.is-inactive {
  opacity: 0.55;
}

/* path-chips: wrap freely so long Thai paths stay fully readable */
.spc__path {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 4px;
}

.spc__chip {
  display: inline-flex;
  align-items: center;
  min-height: 44px; /* iPad touch target — never truncate */
  padding: 6px 10px;
  border: 1px solid var(--c-border, #e4e9f1);
  border-radius: 8px;
  background: var(--c-surface, #fff);
  color: var(--c-text-soft, #5e6c80);
  font-family: var(--c-font, inherit);
  font-size: 14px;
  line-height: 1.25;
  cursor: pointer;
  white-space: normal; /* allow chip text itself to wrap if huge */
  text-align: left;
  transition: background-color var(--c-fast, 140ms) var(--c-ease, ease),
    border-color var(--c-fast, 140ms) var(--c-ease, ease);
}
.spc__chip:hover {
  background: var(--c-blue-soft, #ebf2fa);
  border-color: var(--c-blue, #3b6ea8);
}
.spc__chip:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px var(--c-blue, #3b6ea8);
}
.spc__chip--crumb {
  background: transparent;
  border-color: transparent;
  color: var(--c-text-mute, #8a96a8);
}
.spc__chip--crumb:hover {
  background: var(--c-neutral-soft, #eef0f4);
  border-color: var(--c-border, #e4e9f1);
}
.spc__chip--node {
  color: var(--c-text, #2c3e50);
  font-weight: 600;
  background: var(--c-blue-soft, #ebf2fa);
  border-color: var(--c-blue, #3b6ea8);
}

/* the highlighted (matched) substring */
.spc__chip >>> b {
  font-weight: 800;
  color: var(--c-blue, #3b6ea8);
}

.spc__sep {
  flex: 0 0 auto;
  color: var(--c-text-mute, #8a96a8);
  font-size: 14px;
  padding: 0 1px;
  user-select: none;
}

.spc__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  padding-left: 2px;
}

.spc__dtype {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--c-surface-soft, #fafbfd);
  border: 1px solid var(--c-border, #e4e9f1);
  font-size: 12px;
  color: var(--c-text-soft, #5e6c80);
  white-space: nowrap;
}
.spc__dtype-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--chip-color, #8a96a8);
}

.spc__leaf {
  font-size: 12px;
  color: var(--c-text-mute, #8a96a8);
}
.spc__leaf.is-branch {
  color: var(--c-text-soft, #5e6c80);
}

.spc__state-dot {
  font-size: 10px;
  line-height: 1;
}
.spc__state-dot.is-on {
  color: var(--c-success, #38866b);
}
.spc__state-dot.is-off {
  color: var(--c-text-mute, #8a96a8);
}
.spc__state-tag {
  padding: 1px 7px;
  border-radius: 4px;
  background: var(--c-neutral-soft, #eef0f4);
  color: var(--c-text-soft, #5e6c80);
  font-size: 11px;
  white-space: nowrap;
}

.spc__more {
  align-self: center;
  margin-top: 8px;
  min-height: 44px;
  padding: 8px 16px;
  border: 1px solid var(--c-border, #e4e9f1);
  border-radius: 8px;
  background: var(--c-surface, #fff);
  color: var(--c-blue, #3b6ea8);
  font-family: var(--c-font, inherit);
  font-size: 14px;
  cursor: pointer;
  transition: background-color var(--c-fast, 140ms) var(--c-ease, ease);
}
.spc__more:hover {
  background: var(--c-blue-soft, #ebf2fa);
}
.spc__more:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px var(--c-blue, #3b6ea8);
}
</style>
